import { ensureMdExtension } from '@rbv/shared';
import { useApp } from './store.js';
import { vault } from './sync/vault.js';
import { index } from './worker/client.js';

export interface LinkTarget {
  /** Resolved file id, if known. */
  id?: string;
  /** Raw wikilink target text (used to resolve or create). */
  target?: string;
  /** Path of the note the link lives in (for relative resolution). */
  fromId?: string;
  heading?: string;
  blockId?: string;
}

/**
 * Follow a wikilink: open the target note, or create it when unresolved
 * (Obsidian's click-unresolved-creates). Canvas files open the canvas view.
 */
export async function navigateToLink(t: LinkTarget): Promise<void> {
  const app = useApp.getState();
  let id = t.id ?? null;

  if (!id && t.target) {
    if (t.target === '' && t.fromId) {
      id = t.fromId; // [[#heading]] within the same note
    } else {
      const suggestions = await index.suggestLinks(t.target, t.fromId, 1);
      const exact = suggestions.find(
        (s) => s.kind !== 'heading' && s.linkText.toLowerCase() === t.target!.toLowerCase(),
      );
      id = exact?.id ?? vault.findByPath(ensureMdExtension(t.target));
      if (!id) {
        id = await vault.createNote(ensureMdExtension(t.target));
      }
    }
  }
  if (!id) return;

  const entry = vault.getEntry(id);
  if (entry?.kind === 'canvas') {
    app.openView({ kind: 'canvas', id });
  } else {
    app.openNote(id, { heading: t.heading, blockId: t.blockId });
  }
}

/** URL for serving a vault file (attachments, embeds). */
export const vaultFileUrl = (path: string) =>
  `/vault/${path.split('/').map(encodeURIComponent).join('/')}`;
